import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatEatDateTime, formatEatTime, sectionsFromTemplate } from '@wodi/domain';
import type { NoteSectionInput, NoteView, WorkspaceView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  NoteEditor,
  Notice,
  Section,
  TextArea,
  type NoteEditorSection,
  useShortcut,
} from '@wodi/ui';
import { useEffect, useEffectEvent, useState } from 'react';

import { ApiError } from '../api/client.ts';
import { amendNote, noteHistoryQuery, saveNoteDraft, signNote } from '../api/consultation.ts';
import { sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { noteDraftSent, queueNoteDraft } from '../offline/queued-note.ts';
import { draftStore, noteDraftKey } from '../patients/drafts.ts';
import { toasts } from '../toasts.ts';
import styles from './consultation.module.css';

// The note (plan §7.1): the template's sections as one long form, autosaved as a draft on
// the server every few seconds and on this device on every change (P8), then signed.
// A signed note is read-only; changes are amendments with a reason, and every version
// stays readable in History.

/** How long typing pauses before the draft is sent to the server. */
export const NOTE_AUTOSAVE_MS = 2_000;

type SaveState = 'idle' | 'saving' | 'saved' | 'device' | 'failed';

function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** Where a new note starts: the template's headings, or one plain section without one. */
export function startingSections(view: WorkspaceView): NoteEditorSection[] {
  if (view.template === null) return [{ key: 'note', heading: 'Note', text: '' }];
  return sectionsFromTemplate(view.template.sections).map((s, i) => ({
    ...s,
    hint: view.template?.sections[i]?.hint ?? null,
  }));
}

function toInput(sections: readonly NoteEditorSection[]): NoteSectionInput[] {
  return sections.map((s) => ({ key: s.key, heading: s.heading, text: s.text }));
}

function SignedNote({ sections }: { sections: readonly NoteSectionInput[] }) {
  const written = sections.filter((s) => s.text.trim() !== '');
  return (
    <dl className={styles.signed}>
      {written.map((s) => (
        <div key={s.key} className={styles.signedSection}>
          <dt>{s.heading}</dt>
          <dd>{s.text}</dd>
        </div>
      ))}
    </dl>
  );
}

function HistoryDialog({
  noteId,
  isOpen,
  onOpenChange,
}: {
  noteId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const history = useQuery({ ...noteHistoryQuery(noteId), enabled: isOpen });
  return (
    <Dialog title="Note history" isOpen={isOpen} onOpenChange={onOpenChange}>
      {history.isPending ? <LoadingBlock label="Loading the history" rows={4} /> : null}
      {history.isError ? (
        <Notice tone="error">{errorText(history.error, 'The history could not be loaded.')}</Notice>
      ) : null}
      {history.isSuccess ? (
        <div className={styles.history}>
          {history.data.versions.map((v) => (
            <section key={v.version} className={styles.historyVersion}>
              <p className={styles.subheading}>
                {v.kind === 'signed'
                  ? `Signed ${formatEatDateTime(new Date(v.recordedAt))} by ${v.recordedBy.displayName}`
                  : `Amended ${formatEatDateTime(new Date(v.recordedAt))} by ${v.recordedBy.displayName}: ${v.changeReason ?? ''}`}
              </p>
              <SignedNote sections={v.sections} />
            </section>
          ))}
        </div>
      ) : null}
      <DialogActions>
        <Button
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Writing the note: autosave to the device and the server, then sign. */
function DraftEditor({
  view,
  consultationId,
  note,
}: {
  view: WorkspaceView;
  consultationId: string;
  note: NoteView | undefined;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const key = noteDraftKey(session?.user.id ?? '', session?.facility.id ?? '', consultationId);
  const hints = new Map((view.template?.sections ?? []).map((s) => [s.key, s.hint]));
  const [sections, setSections] = useState<NoteEditorSection[]>(() =>
    note?.sections === null || note?.sections === undefined
      ? startingSections(view)
      : note.sections.map((s) => ({ ...s, hint: hints.get(s.key) ?? null })),
  );
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState<string | null>(note?.draftSavedAt ?? null);
  const [dirty, setDirty] = useState(false);
  const templateId = note?.template?.id ?? view.template?.id ?? null;

  // A draft left on this device (typed offline, or before a refresh) wins over the server's.
  useEffect(() => {
    let cancelled = false;
    void draftStore()
      .get(key)
      .then((stored) => {
        if (cancelled || !Array.isArray(stored)) return;
        setSections(stored as NoteEditorSection[]);
        setDirty(true);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [key]);

  const keepForLater = (current: NoteEditorSection[]) => {
    void queueNoteDraft({
      consultationId,
      visitId: view.visit.id,
      sections: toInput(current),
      templateId,
    });
  };

  const save = useMutation({
    mutationFn: (current: NoteEditorSection[]) =>
      saveNoteDraft(consultationId, { sections: toInput(current), templateId }),
    onMutate: () => {
      setSaveState('saving');
    },
    onSuccess: (result) => {
      setSaveState('saved');
      setSavedAt(result.savedAt);
      void noteDraftSent(consultationId);
    },
    onError: (error, current) => {
      setSaveState('failed');
      // The connection dropped: the retry queue sends it when Wodi is back (ADR 0018).
      if (error instanceof ApiError && error.code === 'offline') keepForLater(current);
    },
  });

  const autosave = useEffectEvent((current: NoteEditorSection[]) => {
    save.mutate(current);
  });
  const queueOffline = useEffectEvent((current: NoteEditorSection[]) => {
    keepForLater(current);
  });

  useEffect(() => {
    if (!dirty) return undefined;
    void draftStore()
      .set(key, sections)
      .catch(() => undefined);
    const timer = window.setTimeout(() => {
      if (online) autosave(sections);
      else queueOffline(sections);
    }, NOTE_AUTOSAVE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [sections, online, key, dirty]);

  const sign = useMutation({
    mutationFn: () => signNote(consultationId, { sections: toInput(sections), templateId }),
    onSuccess: async (next) => {
      queryClient.setQueryData(['visits', view.visit.id, 'workspace'], next);
      await draftStore()
        .delete(key)
        .catch(() => undefined);
      await noteDraftSent(consultationId);
      toasts.show({ title: 'Note signed' });
    },
  });
  const canSign = online && !sign.isPending && sections.some((s) => s.text.trim() !== '');
  useShortcut(
    'mod+enter',
    () => {
      if (canSign) sign.mutate();
    },
    { allowInFields: true, description: 'Sign note' },
  );

  const stateWords: Record<SaveState, string> = {
    idle: savedAt === null ? 'Not saved yet' : `Saved ${formatEatTime(new Date(savedAt))}`,
    saving: 'Saving…',
    saved: savedAt === null ? 'Saved' : `Saved ${formatEatTime(new Date(savedAt))}`,
    device: 'Saved on this device',
    failed: 'Not saved to Wodi yet. Saved on this device; it will try again as you type.',
  };

  return (
    <Section title="Note">
      <NoteEditor
        label="Note being written"
        sections={sections}
        saveState={stateWords[!online && dirty ? 'device' : saveState]}
        onChange={(sectionKey, text) => {
          setDirty(true);
          setSections((prev) => prev.map((s) => (s.key === sectionKey ? { ...s, text } : s)));
        }}
      />
      {view.template?.provisional === true ? (
        <p className={styles.help}>
          {`Template "${view.template.name}" is provisional until your clinical lead approves it.`}
        </p>
      ) : null}
      {!online ? (
        <Notice tone="info">
          Signing needs a connection to Wodi. Your note is saved on this device.
        </Notice>
      ) : null}
      {sign.error === null ? null : (
        <Notice tone="error">
          {errorText(sign.error, 'The note could not be signed. Try again.')}
        </Notice>
      )}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={!canSign}
          isPending={sign.isPending}
          pendingLabel="Signing…"
          onPress={() => {
            sign.mutate();
          }}
        >
          Sign note (Ctrl Enter)
        </Button>
      </div>
    </Section>
  );
}

function AmendDialog({
  view,
  note,
  isOpen,
  onOpenChange,
}: {
  view: WorkspaceView;
  note: NoteView;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [sections, setSections] = useState<NoteEditorSection[]>(() => note.sections ?? []);
  const [reason, setReason] = useState('');
  const amend = useMutation({
    mutationFn: () => amendNote(note.id, { sections: toInput(sections), reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['visits', view.visit.id, 'workspace'] });
      await queryClient.invalidateQueries({ queryKey: ['notes', note.id] });
      onOpenChange(false);
      toasts.show({ title: 'Note amended' });
    },
  });
  return (
    <Dialog title="Amend note" isOpen={isOpen} onOpenChange={onOpenChange}>
      <p className={styles.help}>The signed version stays in the history with your reason.</p>
      <NoteEditor
        label="Amended note"
        sections={sections}
        onChange={(key, text) => {
          setSections((prev) => prev.map((s) => (s.key === key ? { ...s, text } : s)));
        }}
      />
      <TextArea label="Reason for the amendment" value={reason} onChange={setReason} rows={2} />
      {amend.error === null ? null : (
        <Notice tone="error">{errorText(amend.error, 'The note could not be amended.')}</Notice>
      )}
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={reason.trim().length < 5}
          isPending={amend.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            amend.mutate();
          }}
        >
          Save amendment
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** The note of one consultation: the editor for its author, or the signed note. */
export function NotePanel({
  view,
  consultationId,
  mayWrite,
}: {
  view: WorkspaceView;
  consultationId: string;
  mayWrite: boolean;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const [amending, setAmending] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const consultation = view.consultations.find((c) => c.id === consultationId);
  const note = view.notes.find((n) => n.consultationId === consultationId);
  const mine = consultation?.clinician.id === session?.user.id;

  if (note?.status === 'signed' || note?.status === 'entered_in_error') {
    return (
      <section className={styles.panel} aria-labelledby={`note-${consultationId}`}>
        <h2 id={`note-${consultationId}`} className={styles.heading}>
          Note
        </h2>
        <p className={styles.help}>
          {`Signed ${note.signedAt === null ? '' : formatEatDateTime(new Date(note.signedAt))} by ${note.author.displayName}`}
          {note.latestAmendment === null
            ? ''
            : `. Amended ${formatEatDateTime(new Date(note.latestAmendment.at))}: ${note.latestAmendment.reason}`}
        </p>
        {note.sections === null ? null : <SignedNote sections={note.sections} />}
        <div className={styles.actions}>
          {mayWrite && mine && note.status === 'signed' ? (
            <Button
              onPress={() => {
                setAmending(true);
              }}
            >
              Amend
            </Button>
          ) : null}
          <Button
            variant="quiet"
            onPress={() => {
              setShowHistory(true);
            }}
          >
            History
          </Button>
        </div>
        {amending ? (
          <AmendDialog view={view} note={note} isOpen={amending} onOpenChange={setAmending} />
        ) : null}
        <HistoryDialog noteId={note.id} isOpen={showHistory} onOpenChange={setShowHistory} />
      </section>
    );
  }

  if (!mine || !mayWrite || consultation?.status === 'entered_in_error') {
    return (
      <Section title="Note">
        <p className={styles.help}>
          {note === undefined
            ? 'No note written for this consultation.'
            : `Note being written by ${note.author.displayName}. It shows here once signed.`}
        </p>
      </Section>
    );
  }

  return (
    <div className={styles.panel}>
      <DraftEditor view={view} consultationId={consultationId} note={note} />
    </div>
  );
}
